import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import logo from '../assets/logo.png';
import {
    FileText, Briefcase, Brain, Search, Mic, BookOpen,
    ArrowRight, ChevronDown, Sparkles, Zap,
} from 'lucide-react';

function LandingPage() {
    const { t, i18n } = useTranslation();
    const pageRef = useRef(null);
    const currentLanguage = i18n.language?.startsWith('fr') ? 'fr' : 'en';

    const handleLanguageChange = (language) => {
        i18n.changeLanguage(language);
        localStorage.setItem('language', language);
    };

    useEffect(() => {
        const node = pageRef.current;
        if (!node) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('visible');
                    }
                });
            },
            { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
        );

        const targets = node.querySelectorAll('.landing-feature-card, .landing-step, .landing-stat');
        targets.forEach((el) => observer.observe(el));

        return () => observer.disconnect();
    }, []);

    const features = [
        {
            icon: FileText,
            color: 'blue',
            title: t('landing.features.resume.title', 'Resume Analysis'),
            desc: t('landing.features.resume.desc', 'AI-powered ATS scoring, keyword analysis, and HR perspective feedback to make your resume stand out.'),
        },
        {
            icon: Briefcase,
            color: 'purple',
            title: t('landing.features.tracker.title', 'Application Tracker'),
            desc: t('landing.features.tracker.desc', 'Kanban board to organize and track every application from applied to offer — never lose track again.'),
        },
        {
            icon: Brain,
            color: 'green',
            title: t('landing.features.career.title', 'Career Coaching'),
            desc: t('landing.features.career.desc', 'Personalized career roadmaps and skill development plans powered by AI to guide your next move.'),
        },
        {
            icon: Search,
            color: 'amber',
            title: t('landing.features.jobs.title', 'Job Search'),
            desc: t('landing.features.jobs.desc', 'Live job listings from 30+ countries filtered to your profile, skills, and career goals.'),
        },
        {
            icon: Mic,
            color: 'rose',
            title: t('landing.features.interview.title', 'Interview Simulator'),
            desc: t('landing.features.interview.desc', 'Practice with AI interviewers using text or voice, get scored feedback, and build confidence.'),
        },
        {
            icon: BookOpen,
            color: 'cyan',
            title: t('landing.features.study.title', 'Study Buddy'),
            desc: t('landing.features.study.desc', 'Upload documents, ask questions, and get instant AI-powered explanations and summaries.'),
        },
    ];

    const steps = [
        {
            num: '1',
            title: t('landing.steps.one.title', 'Create Your Profile'),
            desc: t('landing.steps.one.desc', 'Upload your resume and set your career goals in minutes.'),
        },
        {
            num: '2',
            title: t('landing.steps.two.title', 'Get AI Insights'),
            desc: t('landing.steps.two.desc', 'Receive personalized analysis, coaching, and job matches.'),
        },
        {
            num: '3',
            title: t('landing.steps.three.title', 'Land Your Role'),
            desc: t('landing.steps.three.desc', 'Track applications, prep for interviews, and succeed.'),
        },
    ];

    const stats = [
        { value: '6', label: t('landing.stats.tools', 'AI-Powered Tools') },
        { value: '30+', label: t('landing.stats.countries', 'Countries Supported') },
        { value: '2', label: t('landing.stats.languages', 'Languages (EN/FR)') },
        { value: '24/7', label: t('landing.stats.availability', 'AI Availability') },
    ];

    return (
        <div className="landing-page" ref={pageRef}>
            <SEO
                title="Career Tools"
                description="Resume analysis, ATS scoring, interview simulator, job search across 30+ countries, career coaching, and study tools. Land your dream role with AI."
                path="/"
            />
            {/* ── Navbar ─────────────────────────────────────── */}
            <nav className="landing-nav">
                <Link to="/landing" className="landing-nav-brand">
                    <img src={logo} alt="Career Platform" />
                    <span>{t('app.name', 'Career Platform')}</span>
                </Link>
                <div className="landing-nav-links">
                    <div className="landing-lang-toggle" role="group" aria-label={t('settings.language', 'Language')}>
                        <button
                            type="button"
                            className={`landing-lang-btn ${currentLanguage === 'en' ? 'active' : ''}`}
                            onClick={() => handleLanguageChange('en')}
                            aria-pressed={currentLanguage === 'en'}
                        >
                            EN
                        </button>
                        <button
                            type="button"
                            className={`landing-lang-btn ${currentLanguage === 'fr' ? 'active' : ''}`}
                            onClick={() => handleLanguageChange('fr')}
                            aria-pressed={currentLanguage === 'fr'}
                        >
                            FR
                        </button>
                    </div>
                    <Link to="/login" className="landing-nav-link outline">
                        {t('auth.sign_in', 'Sign In')}
                    </Link>
                    <Link to="/register" className="landing-nav-link primary">
                        {t('auth.create_account_cta', 'Get Started')}
                    </Link>
                </div>
            </nav>

            {/* ── Hero ───────────────────────────────────────── */}
            <section className="landing-hero">
                <div className="landing-hero-icons">
                    <FileText size={44} className="landing-floating-icon" />
                    <Brain size={40} className="landing-floating-icon" />
                    <Briefcase size={38} className="landing-floating-icon" />
                    <Mic size={36} className="landing-floating-icon" />
                    <Search size={34} className="landing-floating-icon" />
                    <BookOpen size={42} className="landing-floating-icon" />
                </div>

                <div className="landing-hero-content">
                    <div className="landing-hero-badge">
                        <Sparkles size={14} />
                        {t('landing.hero.badge', 'AI-Powered Career Tools')}
                    </div>

                    <h1>
                        {t('landing.hero.title_start', 'Your ')}<span className="highlight">{t('landing.hero.title_highlight', 'AI-Powered')}</span><br />
                        {t('landing.hero.title_end', 'Career Companion')}
                    </h1>

                    <p className="landing-hero-subtitle">
                        {t('landing.hero.subtitle', 'From resume analysis to interview prep — everything you need to land your dream role, powered by artificial intelligence.')}
                    </p>

                    <div className="landing-hero-buttons">
                        <Link to="/register" className="landing-btn landing-btn-primary">
                            {t('landing.hero.cta_primary', 'Get Started Free')}
                            <ArrowRight size={18} />
                        </Link>
                        <Link to="/login" className="landing-btn landing-btn-ghost">
                            {t('landing.hero.cta_secondary', 'Sign In')}
                        </Link>
                    </div>
                </div>

                <div className="landing-scroll-hint">
                    <ChevronDown size={28} />
                </div>
            </section>

            {/* ── Features ───────────────────────────────────── */}
            <section className="landing-section">
                <div className="landing-section-header">
                    <h2>{t('landing.features.title', 'Everything You Need to Succeed')}</h2>
                    <p>{t('landing.features.subtitle', 'Six powerful AI tools designed to accelerate every stage of your career journey.')}</p>
                </div>

                <div className="landing-features-grid">
                    {features.map((f, i) => {
                        const Icon = f.icon;
                        return (
                            <div className="landing-feature-card" key={i}>
                                <div className={`landing-feature-icon ${f.color}`}>
                                    <Icon size={24} />
                                </div>
                                <h3>{f.title}</h3>
                                <p>{f.desc}</p>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* ── How It Works ───────────────────────────────── */}
            <section className="landing-section">
                <div className="landing-section-header">
                    <h2>{t('landing.steps.title', 'How It Works')}</h2>
                    <p>{t('landing.steps.subtitle', 'Get started in three simple steps.')}</p>
                </div>

                <div className="landing-steps">
                    {steps.map((s, i) => (
                        <div className="landing-step" key={i}>
                            <div className="landing-step-number">{s.num}</div>
                            {i < steps.length - 1 && <div className="landing-step-connector" />}
                            <h3>{s.title}</h3>
                            <p>{s.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ── Stats ──────────────────────────────────────── */}
            <section className="landing-section landing-stats-section">
                <div className="landing-stats-grid">
                    {stats.map((s, i) => (
                        <div className="landing-stat" key={i}>
                            <div className="landing-stat-number">{s.value}</div>
                            <div className="landing-stat-label">{s.label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ── CTA ────────────────────────────────────────── */}
            <section className="landing-cta">
                <div className="landing-cta-card">
                    <Zap size={32} style={{ color: '#fbbf24', marginBottom: '1rem' }} />
                    <h2>{t('landing.cta.title', 'Ready to Accelerate Your Career?')}</h2>
                    <p>{t('landing.cta.subtitle', 'Join and get access to all six AI-powered career tools today.')}</p>
                    <Link to="/register" className="landing-btn landing-btn-primary">
                        {t('landing.cta.button', 'Get Started — It\'s Free')}
                        <ArrowRight size={18} />
                    </Link>
                    <span className="landing-cta-hint">
                        {t('landing.cta.hint', 'No credit card required')}
                    </span>
                </div>
            </section>

            {/* ── Footer ─────────────────────────────────────── */}
            <footer className="landing-footer">
                <div className="landing-footer-brand">
                    &copy; {new Date().getFullYear()} {t('app.name', 'Career Platform')}
                </div>
                <div className="landing-footer-links">
                    <Link to="/login">{t('auth.sign_in', 'Sign In')}</Link>
                    <Link to="/register">{t('auth.create_account_cta', 'Sign Up')}</Link>
                    <Link to="/terms">{t('nav.terms', 'Terms')}</Link>
                </div>
            </footer>
        </div>
    );
}

export default LandingPage;
